import React, { useState } from 'react'
import { Modal, Form, Button, Grid, Checkbox, Message, Segment, Input } from 'semantic-ui-react'
import _ from 'lodash'
import * as R from 'ramda'

import { TooltipLabel } from 'components/TooltipLabel'

/**
 * @typedef WebhookSetting
 * @property {number=} id
 * @property {string} name
 * @property {string} url
 * @property {'slack' | 'microsoft_teams'=} type
 * @property {array=} surveys
 *
 * @param {object} props
 * @param {boolean} props.modalOpen
 * @param {function} props.onModalClose
 * @param {WebhookSetting=} props.webhookSetting
 * @param {'slack' | 'microsoft_teams'} props.type
 * @param {function=} props.onCreate
 * @param {function=} props.onUpdate
 * @param {function=} props.onUpdateEvent
 * @param {boolean} props.saveButtonLoading
 */
export default function IntegrationWebhookModal({
  modalOpen,
  onModalClose,
  webhookSetting = { name: '', url: '' },
  type,
  onCreate,
  onUpdate,
  onUpdateEvent,
  saveButtonLoading,
}) {
  const isActionCreate = R.prop('id', webhookSetting) === undefined
  const toTitleCase = (text) => _.startCase(_.toLower(text))
  const title = toTitleCase(type)
  const header = `${title} channel configuration`

  function onSubmit(e) {
    e.preventDefault()
    if (isActionCreate) {
      onCreate(e.target.name.value, e.target.url.value)
    } else {
      onUpdate(webhookSetting.id, e.target.name.value, e.target.url.value)
    }
  }

  const microsoftTeamsArticleLink =
    'https://help.simplesat.io/en/articles/9581676-microsoft-teams-integrations'
  const learnMoreLink =
    type === 'slack' ? 'https://help.simplesat.io/en/articles/4139955' : microsoftTeamsArticleLink
  const placeholder =
    type === 'slack'
      ? 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX'
      : 'https://outlook.office.com/webhook/XXXXXXXXXXXXX/IncomingWebhook/YYYYYYYYYY/ZZZZZZZZZZZZZ'

  return (
    // @ts-ignore
    <Modal size="small" open={modalOpen} onClose={onModalClose}>
      <Modal.Header>{header}</Modal.Header>
      <Modal.Content>
        <p>
          First, choose which channel you’d like to receive feedback at. You’ll need to{' '}
          <a
            href={
              type === 'slack'
                ? 'https://slack.com/services/new/incoming-webhook'
                : microsoftTeamsArticleLink
            }
            target="_blank"
            rel="noreferrer"
          >
            create an Incoming Webhook in {title}.
          </a>
        </p>
        <Form onSubmit={onSubmit}>
          <Form.Field required>
            <label>Friendly name</label>
            <input
              name="name"
              required
              placeholder="Support Team"
              defaultValue={webhookSetting.name}
            />
          </Form.Field>
          <Form.Field required>
            <label>
              Incoming Webhook URL
              <span>
                <TooltipLabel
                  descriptionTitle="Incoming Webhook URL"
                  descriptionContent={
                    <React.Fragment>
                      <p>
                        Create an Incoming Webhook in your {toTitleCase(type)} account, then paste
                        it in the field below.
                      </p>
                    </React.Fragment>
                  }
                  learnMoreLink={learnMoreLink}
                />
              </span>
            </label>
            <textarea
              name="url"
              rows={3}
              required
              placeholder={placeholder}
              defaultValue={webhookSetting.url}
            />
          </Form.Field>
          <Button primary type="submit" loading={saveButtonLoading} disabled={saveButtonLoading}>
            Save settings
          </Button>
        </Form>
        <hr />
        <p>
          Then, choose the surveys and types of notifications you’d like your channel to receive.
        </p>
        {isActionCreate ? (
          <Message>Please add a name and webhook URL to edit survey notification settings.</Message>
        ) : (
          <SurveyWebhookEventsSection
            webhookSetting={webhookSetting}
            onUpdateEvent={onUpdateEvent}
          />
        )}
      </Modal.Content>
    </Modal>
  )
}

function SurveyWebhookEventsSection({ webhookSetting, onUpdateEvent }) {
  const [surveySearch, setSurveySearch] = useState('')
  function handleSearchChange(_, { value }) {
    setSurveySearch(value)
  }

  const filteredSurveys = webhookSetting.surveys.filter((survey) => {
    if (surveySearch) {
      return survey.name.toLowerCase().includes(surveySearch.toLowerCase())
    }

    return true
  })

  return (
    <React.Fragment>
      <Input
        icon="far fa-magnifying-glass"
        iconPosition="left"
        className="search"
        placeholder="Search surveys"
        onChange={handleSearchChange}
      />
      {filteredSurveys.map((survey) => (
        <SurveyWebHookSetting
          key={survey.id}
          survey={survey}
          webhookSetting={webhookSetting}
          onUpdateEvent={onUpdateEvent}
        />
      ))}
    </React.Fragment>
  )
}

function SurveyWebHookSetting({ survey, webhookSetting, onUpdateEvent }) {
  const {
    id,
    name,
    token,
    event: { instant_notification, summary_report },
  } = survey
  const [instantEvent, setInstantEvents] = useState(instant_notification)
  const [summaryEvent, setSummaryEvents] = useState(summary_report)

  function onChangeInstantEvent(_, { name, checked }) {
    setInstantEvents((prevState) => ({ ...prevState, [name]: checked }))
    onUpdateEvent(webhookSetting, 'instant_notification', name, checked, token)
  }

  function onChangeSummaryEvent(_, { name, checked }) {
    setSummaryEvents((prevState) => ({ ...prevState, [name]: checked }))
    onUpdateEvent(webhookSetting, 'summary_report', name, checked, token)
  }

  return (
    <Segment key={id} padded style={{ border: '1px solid rgba(34,36,38,.15)' }}>
      <h3>{name}</h3>
      <Grid>
        <Grid.Row>
          <Grid.Column width={10}>
            <h4>Instant notifications</h4>
            <Form>
              <Form.Group grouped>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.positive_feedback_with_comment}
                    toggle
                    label="Positive with comment"
                    name="positive_feedback_with_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.positive_feedback_without_comment}
                    toggle
                    label="Positive without comment"
                    name="positive_feedback_without_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.neutral_feedback_with_comment}
                    toggle
                    label="Neutral with comment"
                    name="neutral_feedback_with_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.neutral_feedback_without_comment}
                    toggle
                    label="Neutral without comment"
                    name="neutral_feedback_without_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.negative_feedback_with_comment}
                    toggle
                    label="Negative with comment"
                    name="negative_feedback_with_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={instantEvent.negative_feedback_without_comment}
                    toggle
                    label="Negative without comment"
                    name="negative_feedback_without_comment"
                    onChange={onChangeInstantEvent}
                  />
                </Form.Field>
              </Form.Group>
            </Form>
          </Grid.Column>
          <Grid.Column width={6}>
            <h4>Summary reports</h4>
            <Form>
              <Form.Group grouped>
                <Form.Field>
                  <Checkbox
                    checked={summaryEvent.weekly_summary_report}
                    toggle
                    label="Weekly"
                    name="weekly_summary_report"
                    onChange={onChangeSummaryEvent}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    checked={summaryEvent.monthly_summary_report}
                    toggle
                    label="Monthly"
                    name="monthly_summary_report"
                    onChange={onChangeSummaryEvent}
                  />
                </Form.Field>
              </Form.Group>
            </Form>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/IntegrationWebhookModal/index.js